import { Component, OnDestroy, OnInit } from '@angular/core';
import { DpCardMenuPlacement, DpCardTitle, DpChartColor, DpFilterCard, DpFilterOption, DpLineChart, DpLineChartOptions, DpLineXAxisParameters } from '@docpath/webcomponents';
import { LineSeriesData, LineSeriesDataParameters } from '@docpath/webcomponents/shared/chart/models/line-chart.model';
import { Subscription } from 'rxjs';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { Constants } from '../../../../constants/constants';
import { SearchKeyType, StatisticsResult, StatisticsSearch } from '../../models/statistics';
import { HomeService } from '../../services/home.service';
import { HomeHttpService } from '../../services/home-http.service';

@Component({
  selector: 'app-home-chart',
  templateUrl: './home-chart.component.html'
})
export class HomeChartComponent implements OnInit, OnDestroy {

  rightLineChart?: DpLineChart;
  leftLineChart?: DpLineChart;
  showLoadingLeftChart = false;
  showLoadingRightChart = false;

  leftCardFilters = [
    new DpFilterOption({ label: 'home.chart.filter.last-24-hours.label', value: Constants.NUMBER_OF_HOURS_24, tooltip: 'home.chart.filter.last-24-hours.tooltip', active: true }),
    new DpFilterOption({ label: 'home.chart.filter.last-12-hours.label', value: Constants.NUMBER_OF_HOURS_12, tooltip: 'home.chart.filter.last-12-hours.tooltip' }),
    new DpFilterOption({ label: 'home.chart.filter.last-6-hours.label', value: Constants.NUMBER_OF_HOURS_6, tooltip: 'home.chart.filter.last-6-hours.tooltip' }),
    new DpFilterOption({ label: 'home.chart.filter.last-hour.label', value: Constants.NUMBER_OF_HOURS_1, tooltip: 'home.chart.filter.last-hour.tooltip' })
  ];

  rightCardFilters = [
    new DpFilterOption({ label: 'home.chart.filter.last-24-hours.label', value: Constants.NUMBER_OF_HOURS_24, tooltip: 'home.chart.filter.last-24-hours.tooltip', active: true }),
    new DpFilterOption({ label: 'home.chart.filter.last-12-hours.label', value: Constants.NUMBER_OF_HOURS_12, tooltip: 'home.chart.filter.last-12-hours.tooltip' }),
    new DpFilterOption({ label: 'home.chart.filter.last-6-hours.label', value: Constants.NUMBER_OF_HOURS_6, tooltip: 'home.chart.filter.last-6-hours.tooltip' }),
    new DpFilterOption({ label: 'home.chart.filter.last-hour.label', value: Constants.NUMBER_OF_HOURS_1, tooltip: 'home.chart.filter.last-hour.tooltip' })
  ];

  leftFilterCard: DpFilterCard;
  rightFilterCard: DpFilterCard;

  homeRefreshChangedSubscription?: Subscription;

  leftChartValues: StatisticsResult[] = [];
  rightChartValues: StatisticsResult[] = [];

  constructor(
    private readonly homeHttpService: HomeHttpService,
    private readonly homeService: HomeService,
    private readonly cloudService: CloudService
  ) {

    this.leftFilterCard = new DpFilterCard({
      title: new DpCardTitle({
        label: this.cloudService.isModeCloud() ? 'home.chart.generated-transactions.label' : 'home.chart.generated-documents.label',
        tooltip: this.cloudService.isModeCloud() ? 'home.chart.generated-transactions.tooltip' : 'home.chart.generated-documents.tooltip'
      }),
      bodyHeight: '350px',
      filters: this.leftCardFilters
    });

    this.rightFilterCard = new DpFilterCard({
      title: new DpCardTitle({
        label: 'home.chart.average-wait-time.label',
        tooltip: 'home.chart.average-wait-time.tooltip'
      }),
      bodyHeight: '350px',
      menuPlacement: DpCardMenuPlacement.Left,
      filters: this.rightCardFilters
    });

  }

  ngOnInit(): void {
    if (!this.homeRefreshChangedSubscription) {
      this.homeRefreshChangedSubscription = this.homeService.homeRefreshChanged.subscribe(() => {
        this.loadLeftChartValues();
        this.loadRightChartValues();
      });
    }

    this.loadLeftChartValues();
    this.loadRightChartValues();
  }

  ngOnDestroy(): void {
      if (this.homeRefreshChangedSubscription) {
          this.homeRefreshChangedSubscription.unsubscribe();
      }
  }

  loadRightChartValues(filter?: DpFilterOption): void {
    const activeFilter = filter ?? this.rightFilterCard.filters.find(filter => filter.active);

    if (activeFilter) {
      const search = this.toSearch(SearchKeyType.AverageWaitTime, activeFilter.value as number);

      if (!this.rightLineChart) {
        this.showLoadingRightChart = true;
      }
      this.homeHttpService.getStatistics(search).subscribe(result => {
        this.rightChartValues = result;
        this.rightLineChart = this.initLineChart(this.rightChartValues, DpChartColor.Green, DpChartColor.Green, activeFilter.value as number);
        this.showLoadingRightChart = false;
      });
    }
  }

  loadLeftChartValues(filter?: DpFilterOption): void {
    const activeFilter = filter ?? this.leftFilterCard.filters.find(filter => filter.active);

    if (activeFilter) {
      const search = this.toSearch(
        this.cloudService.isModeCloud() ? SearchKeyType.GeneratedTransactions : SearchKeyType.GeneratedDocuments,
        activeFilter.value as number
      );

      if (!this.leftLineChart) {
        this.showLoadingLeftChart = true;
      }
      this.homeHttpService.getStatistics(search).subscribe(result => {
        this.leftChartValues = result;
        this.leftLineChart = this.initLineChart(this.leftChartValues, DpChartColor.Blue, DpChartColor.Blue, activeFilter.value as number);
        this.showLoadingLeftChart = false;
      });
    }
  }

  private toSearch(key: SearchKeyType, hours: number): StatisticsSearch {
    return new StatisticsSearch(key, hours);
  }

  private initLineChart(statisticsResults: StatisticsResult[], lineColor: string, itemColor: string, numberHours: number): DpLineChart {
    const xAxisParameters: DpLineXAxisParameters = { axisLabelFormatter: '{HH} H', minInterval: this.getXAxisMinInterval(numberHours) };
    const yAxisParameters = Math.max(...statisticsResults.map(result => result.yAxis)) < 4 ? { interval: 1 } : undefined;

    const statistics: LineSeriesData[] = statisticsResults.map(result => ({
      name: result.xAxis,
      value: [result.xAxis, result.yAxis]
    }));

    const seriesData: LineSeriesDataParameters[] = [
      { name: '', value: statistics, lineStyle: { width: 3, color: lineColor }, itemStyle: { color: itemColor }, symbolSize: 6 }
    ];

    const lastValue = seriesData[0].value[seriesData[0].value.length - 1].name;

    const lineChartOptions = new DpLineChartOptions({
      xAxisParameters,
      yAxisParameters,
      seriesData,
      showLegend: false,
      tooltipFormatter: (parameters: unknown) => this.getTooltipFormatter(parameters, lastValue)
    });

    return new DpLineChart({ options: lineChartOptions });
  }

  private getXAxisMinInterval(numberHours: number) {
    let xAxisMinInterval;

    switch (numberHours) {
      case Constants.NUMBER_OF_HOURS_24:
        xAxisMinInterval = 8640000;
        break;
      case Constants.NUMBER_OF_HOURS_12:
        xAxisMinInterval = 4320000;
        break;
      case Constants.NUMBER_OF_HOURS_6:
        xAxisMinInterval = 2160000;
        break;
      case Constants.NUMBER_OF_HOURS_1:
        xAxisMinInterval = 3600000;
        break;
      default:
        xAxisMinInterval = 0;
    }

    return xAxisMinInterval;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTooltipFormatter(parameters: any, lastValue: string): string {
    const currentValue = parameters.value[0];
    const date = new Date(parameters.value[0]);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    let formattedTime = '';

    if (currentValue === lastValue) {
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
      formattedTime = `${hours}:${formattedMinutes}:${formattedSeconds}`;
    } else {
      formattedTime = `${(hours < 10 ? '0' : '') + hours}H`;
    }

    const value = parameters.value[1];

    return `${formattedTime}, ${value}`;
  }
}
